import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiGet } from '../services/apiClient';
import { useCurrency } from '../components/CurrencyContext';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import ListingHeaderBar from '../components/packages-listing/ListingHeaderBar';
import FiltersSidebar from '../components/packages-listing/FiltersSidebar';
import MobileFiltersDrawer from '../components/packages-listing/MobileFiltersDrawer';
import ThemeChips from '../components/packages-listing/ThemeChips';
import PackageCard from '../components/packages-listing/PackageCard';
import Pagination from '../components/packages-listing/Pagination';
import PackageSkeleton from '../components/packages-listing/PackageSkeleton';
import EnquiryModal from '../components/packages-listing/EnquiryModal';
import { getPackagesListingConfig } from '../api/packagesListing';
import Breadcrumbs from '../components/shared/Breadcrumbs';

const DEFAULT_LABELS = {
  enquire: 'Enquire now',
  clearFilters: 'Clear filters',
  clearAll: 'Clear all',
  budget: 'Budget',
  travellers: 'Travellers',
  perPerson: 'per person',
  viewDetails: 'View details',
  resultsLabel: 'Showing',
  filtersLabel: 'Filters',
  applyFilters: 'Apply filters',
  resetFilters: 'Reset',
};

const SORT_OPTIONS = [
  { value: 'popularity', label: 'Popularity (Trending)' },
  { value: 'trending', label: 'Trending' },
  { value: 'topRated', label: 'Top rated' },
  { value: 'priceAsc', label: 'Price low-high' },
  { value: 'priceDesc', label: 'Price high-low' },
  { value: 'durationAsc', label: 'Shortest duration' },
  { value: 'newest', label: 'Newest' },
  { value: 'recommended', label: 'Recommended' },
];

const parseArrayParam = (value) =>
  value ? value.split(',').map((item) => item.trim()).filter(Boolean) : [];

const buildDefaultFilters = (defaultSort) => ({
  destination: [],
  startDate: '',
  endDate: '',
  travellers: '',
  budgetMin: '',
  budgetMax: '',
  durationMin: '',
  durationMax: '',
  ratingMin: '',
  themes: [],
  featured: false,
  trending: false,
  topRated: false,
  search: '',
  sort: defaultSort || 'popularity',
  page: 1,
  limit: 12,
});

const parseFiltersFromParams = (params, defaultSort) => ({
  destination: parseArrayParam(params.get('destination') || params.get('location') || ''),
  startDate: params.get('startDate') || '',
  endDate: params.get('endDate') || '',
  travellers: params.get('travellers') || params.get('travelers') || '',
  budgetMin: params.get('budgetMin') || params.get('minPrice') || '',
  budgetMax: params.get('budgetMax') || params.get('maxPrice') || '',
  durationMin: params.get('durationMin') || params.get('minDuration') || '',
  durationMax: params.get('durationMax') || params.get('maxDuration') || '',
  ratingMin: params.get('ratingMin') || params.get('rating') || '',
  themes: parseArrayParam(params.get('themes') || ''),
  featured: params.get('featured') === 'true',
  trending: params.get('trending') === 'true',
  topRated: params.get('topRated') === 'true',
  search: params.get('search') || params.get('q') || '',
  sort: params.get('sort') || defaultSort || 'popularity',
  page: Number(params.get('page') || 1),
  limit: Number(params.get('limit') || params.get('pageSize') || 12),
});

const buildParamsFromFilters = (filters) => {
  const params = new URLSearchParams();
  if (filters.destination?.length) params.set('destination', filters.destination.join(','));
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  if (filters.travellers) params.set('travellers', filters.travellers);
  if (filters.budgetMin !== '') params.set('budgetMin', filters.budgetMin);
  if (filters.budgetMax !== '') params.set('budgetMax', filters.budgetMax);
  if (filters.durationMin !== '') params.set('durationMin', filters.durationMin);
  if (filters.durationMax !== '') params.set('durationMax', filters.durationMax);
  if (filters.ratingMin !== '') params.set('ratingMin', filters.ratingMin);
  if (filters.themes?.length) params.set('themes', filters.themes.join(','));
  if (filters.featured) params.set('featured', 'true');
  if (filters.trending) params.set('trending', 'true');
  if (filters.topRated) params.set('topRated', 'true');
  if (filters.search) params.set('search', filters.search);
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  return params;
};

const areFiltersEqual = (a, b) => {
  const keys = Object.keys(a);
  for (const key of keys) {
    const valA = a[key];
    const valB = b[key];
    if (Array.isArray(valA) || Array.isArray(valB)) {
      const arrA = Array.isArray(valA) ? valA : [];
      const arrB = Array.isArray(valB) ? valB : [];
      if (arrA.length !== arrB.length) return false;
      for (let i = 0; i < arrA.length; i += 1) {
        if (arrA[i] !== arrB[i]) return false;
      }
    } else if (String(valA) !== String(valB)) {
      return false;
    }
  }
  return true;
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'REPLACE':
      return action.payload;
    default:
      return state;
  }
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
};

const PackagesListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState(
    searchParams.get('view') === 'list' ? 'list' : 'grid'
  );
  const { formatCurrency } = useCurrency();
  const [config, setConfig] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [themes, setThemes] = useState([]);
  const [packages, setPackages] = useState([]);
  const [facets, setFacets] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [appendMode, setAppendMode] = useState(false);
  const [enquiryPkg, setEnquiryPkg] = useState(null);

  const defaultSort = config?.default_sort || config?.defaultSort || 'popularity';
  const defaultLimit = Number(config?.page_size || config?.pageSize || 12);
  const defaultFilters = useMemo(() => ({ ...buildDefaultFilters(defaultSort), limit: defaultLimit }), [defaultLimit, defaultSort]);
  const [filters, dispatch] = useReducer(reducer, parseFiltersFromParams(searchParams, defaultSort));
  const filtersRef = useRef(filters);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const updateFilters = (patch, options = {}) => {
    const current = filtersRef.current;
    const resetPage = options.resetPage !== false;
    const next = {
      ...current,
      ...patch,
      page: resetPage ? 1 : patch.page ?? current.page,
    };
    if (!options.append) setAppendMode(false);
    dispatch({ type: 'REPLACE', payload: next });
    const nextParams = buildParamsFromFilters(next);
    nextParams.set('view', viewMode);
    setSearchParams(nextParams, { replace: true });
  };

  const resetFilters = () => {
    const next = { ...defaultFilters, limit: filtersRef.current.limit || 12 };
    setAppendMode(false);
    dispatch({ type: 'REPLACE', payload: next });
    const nextParams = new URLSearchParams();
    nextParams.set('view', viewMode);
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    const parsed = parseFiltersFromParams(searchParams, defaultSort);
    if (!areFiltersEqual(parsed, filtersRef.current)) {
      dispatch({ type: 'REPLACE', payload: parsed });
    }
  }, [searchParams, defaultSort]);

  useEffect(() => {
    const nextView = searchParams.get('view');
    if (nextView === 'grid' || nextView === 'list') {
      setViewMode(nextView);
    }
  }, [searchParams]);

  useEffect(() => {
    let active = true;
    const normalizeLabels = (value) => {
      if (!value) return { ...DEFAULT_LABELS };
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return { ...DEFAULT_LABELS, ...(parsed || {}) };
        } catch (_) {
          return { ...DEFAULT_LABELS };
        }
      }
      if (typeof value === 'object') return { ...DEFAULT_LABELS, ...value };
      return { ...DEFAULT_LABELS };
    };
    (async () => {
      try {
        const res = await getPackagesListingConfig();
        const data = res?.data || res || {};
        if (!active) return;
        setConfig({
          title: data.title || 'Tailor-made packages',
          subtitle: data.subtitle || '',
          banner_title: data.banner_title || data.bannerTitle || data.title || 'Tailor-made packages',
          banner_subtitle: data.banner_subtitle || data.bannerSubtitle || data.subtitle || '',
          banner_image_url: data.banner_image_url || data.bannerImageUrl || '',
          default_sort: data.default_sort || data.defaultSort || 'popularity',
          show_trending_row: data.show_trending_row ?? data.showTrendingRow ?? true,
          show_theme_chips: data.show_theme_chips ?? data.showThemeChips ?? true,
          results_mode: data.results_mode || data.resultsMode || 'pagination',
          page_size: Number(data.page_size || data.pageSize || 12),
          card_style: data.card_style || data.cardStyle || 'compact-premium',
          filter_visibility: data.filter_visibility || data.filterVisibility || {},
          show_quick_badges: data.show_quick_badges ?? data.showQuickBadges ?? true,
          show_enquiry_cta: data.show_enquiry_cta ?? data.showEnquiryCta ?? true,
          show_reviews_count: data.show_reviews_count ?? data.showReviewsCount ?? true,
          seo_title: data.seo_title || data.seoTitle || '',
          seo_description: data.seo_description || data.seoDescription || '',
          seo_image: data.seo_image || data.seoImage || '',
          labels: normalizeLabels(data.labels),
        });
      } catch (_) {
        if (!active) return;
        setConfig({
          title: 'Tailor-made packages',
          subtitle: 'Curated journeys crafted for every traveller.',
          banner_title: 'Tailor-made packages',
          banner_subtitle: 'Curated journeys crafted for every traveller.',
          banner_image_url: '',
          default_sort: 'popularity',
          show_trending_row: true,
          show_theme_chips: true,
          results_mode: 'pagination',
          page_size: 12,
          card_style: 'compact-premium',
          filter_visibility: {},
          show_quick_badges: true,
          show_enquiry_cta: true,
          show_reviews_count: true,
          labels: { ...DEFAULT_LABELS },
        });
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [destRes, themeRes] = await Promise.all([
          apiGet('/api/public/package-destinations'),
          apiGet('/api/public/package-themes'),
        ]);
        if (!active) return;
        setDestinations(destRes?.data || destRes || []);
        setThemes(themeRes?.data || themeRes || []);
      } catch (_) {
        if (!active) return;
        setDestinations([]);
        setThemes([]);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const sortParam = searchParams.get('sort');
    if (!sortParam && defaultSort) {
      updateFilters({ sort: defaultSort }, { resetPage: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSort]);

  const apiParamString = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('view');
    return params.toString();
  }, [searchParams]);

  useEffect(() => {
    const params = apiParamString;
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiGet(`/api/packages?${params}`);
        if (!active) return;
        const items = res?.data || res?.items || res?.data?.items || [];
        const pageInfo = res?.meta || res?.pagination || {};
        setPagination({
          page: Number(pageInfo.page || 1),
          limit: Number(pageInfo.limit || pageInfo.pageSize || filtersRef.current.limit || 12),
          total: Number(pageInfo.total || items.length || 0),
          pages: Number(pageInfo.pages || Math.ceil(Number(pageInfo.total || items.length || 0) / Number(pageInfo.limit || pageInfo.pageSize || filtersRef.current.limit || 12))),
        });
        setFacets(res?.meta?.facets || res?.facets || null);
        setPackages((prev) => (appendMode ? [...prev, ...items] : items));
      } catch (err) {
        if (!active) return;
        setError(err.message || 'Unable to load packages');
        setPackages([]);
      } finally {
        if (active) setLoading(false);
        setAppendMode(false);
      }
    }, 400);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [apiParamString, appendMode]);

  useEffect(() => {
    if (!config) return;
    const title = config.seo_title || config.title || 'Tailor-made packages';
    const description =
      config.seo_description ||
      'Browse Homevilas travel packages with curated stays, experiences, and bespoke itineraries.';
    const image = config.seo_image || config.seoImage;
    document.title = title;
    const setMeta = (name, content, attr = 'name') => {
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    setMeta('description', description);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    if (image) {
      setMeta('og:image', resolveAssetUrl(image) || image, 'property');
    }
  }, [config]);

  const labels = { ...DEFAULT_LABELS, ...(config?.labels || {}) };
  const resultsMode = config?.results_mode || config?.resultsMode || 'pagination';

  const destinationLabelMap = useMemo(
    () => new Map(destinations.map((d) => [d.slug || d.name, d.name])),
    [destinations]
  );
  const themeLabelMap = useMemo(
    () => new Map(themes.map((t) => [t.slug || String(t.id), t.name])),
    [themes]
  );

  const chips = useMemo(() => {
    const list = [];
    if (filters.destination.length) {
      const names = filters.destination.map((val) => destinationLabelMap.get(val) || val).join(', ');
      list.push({ key: 'destination', label: `Destination: ${names}`, onRemove: () => updateFilters({ destination: [] }) });
    }
    if (filters.startDate || filters.endDate) {
      const start = formatDate(filters.startDate);
      const end = formatDate(filters.endDate);
      const label = end ? `Dates: ${start} - ${end}` : `Date: ${start}`;
      list.push({ key: 'dates', label, onRemove: () => updateFilters({ startDate: '', endDate: '' }) });
    }
    if (filters.travellers) {
      list.push({ key: 'travellers', label: `Travellers: ${filters.travellers}`, onRemove: () => updateFilters({ travellers: '' }) });
    }
    if (filters.budgetMin || filters.budgetMax) {
      const min = filters.budgetMin ? formatCurrency(filters.budgetMin, 'INR') : 'Min';
      const max = filters.budgetMax ? formatCurrency(filters.budgetMax, 'INR') : 'Max';
      list.push({ key: 'budget', label: `Budget: ${min} - ${max}`, onRemove: () => updateFilters({ budgetMin: '', budgetMax: '' }) });
    }
    if (filters.themes.length) {
      const names = filters.themes.map((val) => themeLabelMap.get(val) || val).join(', ');
      list.push({ key: 'themes', label: `Themes: ${names}`, onRemove: () => updateFilters({ themes: [] }) });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, destinationLabelMap, themeLabelMap]);

  const showing = packages.length;
  const total = pagination.total || 0;
  const bannerImage = config?.banner_image_url
    ? `url(${resolveAssetUrl(config.banner_image_url) || config.banner_image_url})`
    : null;

  const detailQuery = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('view');
    params.set('from', 'list');
    return params.toString();
  }, [searchParams]);

  const onPageChange = (page) => updateFilters({ page }, { resetPage: false });
  const onLimitChange = (limit) => updateFilters({ limit }, { resetPage: true });

  const handleThemeToggle = (value) => {
    const active = filters.themes.includes(value);
    const next = active ? filters.themes.filter((t) => t !== value) : [...filters.themes, value];
    updateFilters({ themes: next });
  };

  const handleViewChange = (nextView) => {
    setViewMode(nextView);
    const nextParams = buildParamsFromFilters(filtersRef.current);
    nextParams.set('view', nextView);
    setSearchParams(nextParams, { replace: true });
  };

  const handleLoadMore = () => {
    setAppendMode(true);
    updateFilters({ page: Number(filters.page || 1) + 1 }, { resetPage: false, append: true });
  };

  const enquiryDefaults = useMemo(
    () => ({
      travelStartDate: filters.startDate || '',
      travelEndDate: filters.endDate || '',
      travellersCount: filters.travellers || '',
      budgetMin: filters.budgetMin || '',
      budgetMax: filters.budgetMax || '',
      utmSource: searchParams.get('utm_source') || searchParams.get('utmSource') || '',
      utmCampaign: searchParams.get('utm_campaign') || searchParams.get('utmCampaign') || '',
    }),
    [
      filters.startDate,
      filters.endDate,
      filters.travellers,
      filters.budgetMin,
      filters.budgetMax,
      searchParams,
    ]
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#faf7f2]">
      <div
        className="relative overflow-hidden"
        style={
          bannerImage
            ? {
                backgroundImage: `linear-gradient(120deg, rgba(8,27,58,0.92), rgba(12,46,82,0.82), rgba(15,118,110,0.72)), ${bannerImage}`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : { backgroundImage: 'linear-gradient(120deg, #081b3a 0%, #0b2c4f 45%, #0f766e 100%)' }
        }
      >
        <div className="mx-auto max-w-[92rem] px-4 py-12 text-white md:py-14">
          <Breadcrumbs
            items={[
              { label: 'Packages', to: '/packages' },
              { label: 'Listing' },
            ]}
            className="text-white/70"
            linkClassName="text-white/90"
            separatorClassName="text-white/40"
          />
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-amber-200/80">Homevilas Journeys</p>
          <h2 className="mt-4 font-homevilas text-4xl font-semibold md:text-5xl">
            {config?.banner_title || config?.title || 'Tailor-made packages'}
          </h2>
          <p className="mt-3 max-w-3xl text-sm text-white/75 md:text-base">
            {config?.banner_subtitle || config?.subtitle || 'Luxury itineraries designed for immersive stays and unforgettable experiences.'}
          </p>
        </div>
      </div>

      <ListingHeaderBar
        title={config?.title || 'Tailor-made packages'}
        subtitle={config?.subtitle || ''}
        chips={chips}
        onClearAll={resetFilters}
        showing={showing}
        total={total}
        viewMode={viewMode}
        onViewChange={handleViewChange}
        sort={filters.sort}
        onSortChange={(value) => updateFilters({ sort: value })}
        sortOptions={SORT_OPTIONS}
        labels={labels}
      />

      <main className="mx-auto w-full max-w-[92rem] px-4 py-10">
        <div className="flex min-w-0 flex-col gap-6 lg:flex-row lg:items-start">
          <FiltersSidebar
            filters={filters}
            destinations={destinations}
            themes={themes}
            facets={facets}
            labels={labels}
            visibility={config?.filter_visibility || config?.filterVisibility || {}}
            onChange={(patch) => updateFilters(patch)}
            onReset={resetFilters}
          />

          <div className="min-w-0 flex-1 space-y-6">
            <div className="rounded-[28px] border border-[#dde3ef] bg-white p-4 shadow-[0_14px_36px_rgba(15,23,42,0.05)]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Results</p>
                  <p className="mt-1 text-sm text-slate-600">
                    {labels.resultsLabel || 'Showing'} {showing} of {total}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <span>Page size</span>
                  <select
                    value={filters.limit}
                    onChange={(e) => onLimitChange(Number(e.target.value))}
                    className="rounded-full border border-slate-200 px-3 py-2 text-xs"
                  >
                    {[defaultLimit, 24, 36, 48]
                      .filter((size, index, arr) => arr.indexOf(size) === index)
                      .map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] lg:hidden"
            >
              <i className="bi bi-sliders" />
              Filters
            </button>

            {(config?.show_theme_chips || config?.showThemeChips) && themes.length ? (
              <ThemeChips themes={themes} activeThemes={filters.themes} onToggle={handleThemeToggle} />
            ) : null}

            <div
              className={
                viewMode === 'grid'
                  ? 'grid gap-5 md:grid-cols-2 xl:grid-cols-3'
                  : 'flex flex-col gap-4'
              }
            >
              {loading
                ? Array.from({ length: 6 }).map((_, idx) => <PackageSkeleton key={`skel-${idx}`} view={viewMode} />)
                : packages.map((pkg) => (
                    <PackageCard
                      key={pkg.id}
                      pkg={pkg}
                      view={viewMode}
                      onEnquire={(item) => setEnquiryPkg(item)}
                      labels={labels}
                      formatPrice={formatCurrency}
                      linkQuery={detailQuery}
                      showQuickBadges={config?.show_quick_badges ?? config?.showQuickBadges ?? true}
                      showEnquiryCta={config?.show_enquiry_cta ?? config?.showEnquiryCta ?? true}
                      showReviewsCount={config?.show_reviews_count ?? config?.showReviewsCount ?? true}
                    />
                  ))}
            </div>

            {!loading && error ? (
              <div className="rounded-3xl border border-rose-100 bg-rose-50 p-6 text-sm text-rose-700">{error}</div>
            ) : null}

            {!loading && !error && !packages.length ? (
              <div className="rounded-[28px] border border-[#dde3ef] bg-white p-10 text-center shadow-[0_14px_36px_rgba(15,23,42,0.05)]">
                <p className="text-lg font-semibold text-slate-900">No packages matched these filters.</p>
                <p className="mt-2 text-sm text-slate-500">
                  Clear the current filters or broaden your destination, duration, or budget range.
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 rounded-full border border-[#d9d0c1] px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-700"
                >
                  {labels.resetFilters || 'Reset'}
                </button>
              </div>
            ) : null}

            {resultsMode === 'pagination' ? (
              <Pagination page={pagination.page} pages={pagination.pages} onChange={onPageChange} />
            ) : null}

            {resultsMode === 'loadmore' && pagination.page < pagination.pages ? (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="rounded-full border border-slate-200 px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em]"
                >
                  Load more
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </main>

      <MobileFiltersDrawer
        open={drawerOpen}
        filters={filters}
        defaultFilters={defaultFilters}
        onClose={() => setDrawerOpen(false)}
        onApply={(next) => updateFilters(next)}
        destinations={destinations}
        themes={themes}
        facets={facets}
        visibility={config?.filter_visibility || config?.filterVisibility || {}}
        labels={labels}
      />

      <EnquiryModal
        open={!!enquiryPkg}
        onClose={() => setEnquiryPkg(null)}
        pkg={enquiryPkg}
        labels={labels}
        source="LISTING"
        initialValues={enquiryDefaults}
      />
    </div>
  );
};

export default PackagesListPage;
